import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import './AddClearanceFileModal.css';

// mode: 'arrival' (expected_arrival_date only) | 'inspection' (inspection_date + inspection_time)
const DateEditModal = ({ isOpen, onClose, file, mode = 'arrival', onSuccess }) => {
    const emptyDate = { day: '', month: '', year: '' };
    const emptyTime = { hour: '', minute: '' };

    const [arrivalDate, setArrivalDate] = useState(emptyDate);
    const [inspectionDate, setInspectionDate] = useState(emptyDate);
    const [inspectionTime, setInspectionTime] = useState(emptyTime);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const isoToParts = (iso) => {
        if (!iso) return emptyDate;
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
        if (!m) return emptyDate;
        return { year: m[1], month: m[2], day: m[3] };
    };

    const timeToParts = (time) => {
        if (!time) return emptyTime;
        const m = /^(\d{2}):(\d{2})/.exec(time);
        if (!m) return emptyTime;
        return { hour: m[1], minute: m[2] };
    };

    useEffect(() => {
        if (!isOpen || !file) return;
        setArrivalDate(isoToParts(file.expected_arrival_date));
        setInspectionDate(isoToParts(file.inspection_date));
        setInspectionTime(timeToParts(file.inspection_time));
        setError('');
    }, [isOpen, file]);

    const handleDateChange = (target, field, value) => {
        const numeric = value.replace(/\D/g, '');
        const maxLength = field === 'year' ? 4 : 2;
        const maxValue = field === 'day' ? 31 : field === 'month' ? 12 : 9999;
        let v = numeric.slice(0, maxLength);
        if (v && parseInt(v, 10) > maxValue) v = String(maxValue);

        if (target === 'arrival') setArrivalDate(prev => ({ ...prev, [field]: v }));
        else setInspectionDate(prev => ({ ...prev, [field]: v }));
        if (error) setError('');
    };

    const handleTimeChange = (field, value) => {
        const numeric = value.replace(/\D/g, '');
        const maxValue = field === 'hour' ? 23 : 59;
        let v = numeric.slice(0, 2);
        if (v && parseInt(v, 10) > maxValue) v = String(maxValue);
        setInspectionTime(prev => ({ ...prev, [field]: v }));
    };

    const handleDatePickerChange = (target, e) => {
        const value = e.target.value;
        if (!value) return;
        const [year, month, day] = value.split('-');
        if (target === 'arrival') setArrivalDate({ day, month, year });
        else setInspectionDate({ day, month, year });
    };

    const partsToIso = (parts) => {
        if (!parts.day || !parts.month || !parts.year || parts.year.length !== 4) return null;
        return `${parts.year}-${parts.month.padStart(2, '0')}-${parts.day.padStart(2, '0')}`;
    };

    const isPartial = (parts) => (parts.day || parts.month || parts.year) && !partsToIso(parts);

    const handleSave = async () => {
        if (!file) return;
        setSaving(true);
        setError('');
        try {
            const arrivalIso = partsToIso(arrivalDate);
            const inspectionIso = partsToIso(inspectionDate);
            const timeStr = (inspectionTime.hour && inspectionTime.minute)
                ? `${inspectionTime.hour.padStart(2, '0')}:${inspectionTime.minute.padStart(2, '0')}`
                : null;

            if (mode === 'arrival' && isPartial(arrivalDate)) {
                setError('الرجاء إكمال التاريخ بشكل صحيح أو تركه فارغاً');
                setSaving(false);
                return;
            }
            if (mode === 'inspection') {
                if (isPartial(inspectionDate)) {
                    setError('الرجاء إكمال تاريخ الفحص بشكل صحيح أو تركه فارغاً');
                    setSaving(false);
                    return;
                }
                if (inspectionIso && !timeStr) {
                    setError('الرجاء إدخال وقت الفحص');
                    setSaving(false);
                    return;
                }
                if (!inspectionIso && timeStr) {
                    setError('الرجاء إدخال تاريخ الفحص أو مسح الوقت');
                    setSaving(false);
                    return;
                }
            }

            const payload = mode === 'arrival'
                ? { expected_arrival_date: arrivalIso }
                : { inspection_date: inspectionIso, inspection_time: timeStr };

            const response = await axiosInstance.put(`/clearance-files/${file.id}/`, payload);
            const savedFile = response.data;

            const arrivalChanged = mode === 'arrival' && arrivalIso !== (file.expected_arrival_date || null);
            const inspectionChanged = mode === 'inspection' && (
                inspectionIso !== (file.inspection_date || null) ||
                timeStr !== (file.inspection_time || null)
            );

            if (onSuccess) onSuccess({
                arrivalChanged,
                inspectionChanged,
                newArrival: arrivalIso,
                newInspectionDate: inspectionIso,
                newInspectionTime: timeStr,
                savedFile: savedFile
            });
            onClose();
        } catch (err) {
            console.error('Error updating dates:', err);
            setError('حدث خطأ أثناء حفظ التواريخ');
        }
        setSaving(false);
    };

    if (!isOpen) return null;

    const arrivalPickerId = 'date-edit-arrival-picker';
    const inspectionPickerId = 'date-edit-inspection-picker';

    const title = mode === 'arrival' ? 'تعديل تاريخ الوصول المتوقع' : 'تعديل موعد الفحص';

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2100 }}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', width: '95vw', padding: 0, overflow: 'hidden', borderRadius: '12px' }}>
                <div className="modal-header" style={{ background: '#03428d', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '12px 12px 0 0' }}>
                    <h3 style={{ color: '#fff', margin: 0, fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <svg style={{ width: 20, height: 20 }} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {title} {file?.file_num ? `(${file.file_num})` : ''}
                    </h3>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 5 }}>
                        <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: 20, height: 20 }}>
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>

                <div style={{ padding: '20px' }}>
                    <div className="form-section" style={{ marginBottom: 0 }}>
                        {mode === 'arrival' && (
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">تاريخ الوصول المتوقع</label>
                                    <div className="custom-date-input">
                                        <div className="date-display">
                                            <div className="date-field day-field">
                                                <span className="date-field-label">اليوم</span>
                                                <input type="text" className="date-field-input" placeholder="DD" value={arrivalDate.day} onChange={(e) => handleDateChange('arrival', 'day', e.target.value)} maxLength={2} />
                                            </div>
                                            <span className="date-separator">-</span>
                                            <div className="date-field month-field">
                                                <span className="date-field-label">الشهر</span>
                                                <input type="text" className="date-field-input" placeholder="MM" value={arrivalDate.month} onChange={(e) => handleDateChange('arrival', 'month', e.target.value)} maxLength={2} />
                                            </div>
                                            <span className="date-separator">-</span>
                                            <div className="date-field year-field">
                                                <span className="date-field-label">السنة</span>
                                                <input type="text" className="date-field-input" placeholder="YYYY" value={arrivalDate.year} onChange={(e) => handleDateChange('arrival', 'year', e.target.value)} maxLength={4} />
                                            </div>
                                            <button type="button" className="calendar-trigger" onClick={() => document.getElementById(arrivalPickerId)?.showPicker?.()}>
                                                <svg fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                            <input type="date" id={arrivalPickerId} className="hidden-date-input" value={partsToIso(arrivalDate) || ''} onChange={(e) => handleDatePickerChange('arrival', e)} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {mode === 'inspection' && (
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">تاريخ الفحص</label>
                                    <div className="custom-date-input">
                                        <div className="date-display">
                                            <div className="date-field day-field">
                                                <span className="date-field-label">اليوم</span>
                                                <input type="text" className="date-field-input" placeholder="DD" value={inspectionDate.day} onChange={(e) => handleDateChange('inspection', 'day', e.target.value)} maxLength={2} />
                                            </div>
                                            <span className="date-separator">-</span>
                                            <div className="date-field month-field">
                                                <span className="date-field-label">الشهر</span>
                                                <input type="text" className="date-field-input" placeholder="MM" value={inspectionDate.month} onChange={(e) => handleDateChange('inspection', 'month', e.target.value)} maxLength={2} />
                                            </div>
                                            <span className="date-separator">-</span>
                                            <div className="date-field year-field">
                                                <span className="date-field-label">السنة</span>
                                                <input type="text" className="date-field-input" placeholder="YYYY" value={inspectionDate.year} onChange={(e) => handleDateChange('inspection', 'year', e.target.value)} maxLength={4} />
                                            </div>
                                            <button type="button" className="calendar-trigger" onClick={() => document.getElementById(inspectionPickerId)?.showPicker?.()}>
                                                <svg fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                            <input type="date" id={inspectionPickerId} className="hidden-date-input" value={partsToIso(inspectionDate) || ''} onChange={(e) => handleDatePickerChange('inspection', e)} />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">وقت الفحص</label>
                                    <div className="custom-date-input">
                                        <div className="date-display">
                                            <div className="date-field day-field">
                                                <span className="date-field-label">ساعة</span>
                                                <input type="text" className="date-field-input" placeholder="HH" value={inspectionTime.hour} onChange={(e) => handleTimeChange('hour', e.target.value)} maxLength={2} />
                                            </div>
                                            <span className="date-separator">:</span>
                                            <div className="date-field day-field">
                                                <span className="date-field-label">دقيقة</span>
                                                <input type="text" className="date-field-input" placeholder="MM" value={inspectionTime.minute} onChange={(e) => handleTimeChange('minute', e.target.value)} maxLength={2} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {error && (
                        <div style={{ marginTop: 14, padding: 10, background: '#fff5f5', border: '1px solid #dc3545', borderRadius: 6, color: '#dc3545', fontSize: 13 }}>
                            {error}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 20, paddingTop: 14, borderTop: '1px solid #eef2f7' }}>
                        <button type="button" onClick={handleSave} disabled={saving} style={{ padding: '10px 22px', fontSize: 14, fontWeight: 600, background: '#03428d', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <svg style={{ width: 18, height: 18 }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>{saving ? 'جاري الحفظ...' : 'حفظ'}</span>
                        </button>
                        <button type="button" onClick={onClose} disabled={saving} style={{ padding: '10px 22px', fontSize: 14, fontWeight: 600, background: '#fff', color: '#333', border: '2px solid #ddd', borderRadius: 8, cursor: 'pointer' }}>
                            إلغاء
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DateEditModal;
